import { Theme, Box, Stack, useMediaQuery } from '@mui/material';
import React, { FC, useState, useEffect, useCallback } from 'react';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import { TwButton, TwButtonType } from '../TwButtons/TwButtons';
import TwDialog from '../TwDialog/TwDialog';
import TwShareHistory from '../TwLoadShareHistory/TwLoadShareHistory';
import './TwNotifyDriver.scss';
import { useGetDriversQuery } from '../../redux/api/driver/driverApi';

import { ShareType } from '../../redux/api/load/load.model';
import { logRocketTrack } from '../../utils/logRocketUtils';
import useAuth from '../../providers/Auth/Auth.provider';
import { Trip } from '../../redux/api/trips/trips.model';
import { MESSAGE_TYPE, Order, OrderSharePreviewParams } from '../../redux/api/orders/orders.model';

import { useGetDriverLinksForTripQuery, useShareTripMutation } from '../../redux/api/trips/tripApi';
import { useAppSelector } from '../../redux/hooks';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useGetDriverLinksForOrderQuery } from '../../redux/api/orders/ordersApi';
import DriverNotificationPreferenceList from './DriverNotificationPreferenceList';
import useCanRemoveDrivers from './hooks/useCanRemoveDriver';
import useDriverLabels from './hooks/useDriverLabels';
import useDriverPayload from './hooks/useDriverPayload';
import ShareTripSMS from '../../utils/shareSMS/shareSMS';
export interface INotifyDriverWithFullSegment {
  driverId: string;
  driverIndex: number;
  fullSegment: Trip;
  isForExistingLoadUpdate?: boolean;
  deleted_trip_index?: number;
  removed?: boolean;
  textMessage?: string;
}

interface INotifyDriverProps {
  driversToNotify: INotifyDriverWithFullSegment[];
  unassignedDriversToNotify: INotifyDriverWithFullSegment[];
  orderId?: string;
  tripId?: string;
  open: boolean;
  onClose: () => void;
  isNormalShare?: boolean; // Only shares load on demand
  isSegmentShare?: boolean; // Only shares load segment with its driver
  isSplitLoad: boolean;
  isForNewLoad?: boolean;
}

const TwNotifyDriver: FC<INotifyDriverProps> = ({
  driversToNotify,
  unassignedDriversToNotify,
  orderId,
  tripId,
  open,
  onClose,
  isSplitLoad
}) => {
  const {
    getTenantSetting,
    data: { userData }
  } = useAuth();

  const use24HourClock = getTenantSetting('use-24-hour-clock', false);
  const useCustomerOrderNumber = getTenantSetting('customer-load-nbr-as-primary');

  const filterParams = useAppSelector((state) => state.activeTrips);
  const { addNotification } = useNotifications();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const [assignedPayload, setAssignedPayload] = useState<OrderSharePreviewParams[]>([]);

  const [unassignedPayload, setUnassignedPayload] = useState<OrderSharePreviewParams[]>([]);

  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const { data: drivers = { content: [], total: 0 } } = useGetDriversQuery({});

  const [shareTripPreview] = useShareTripMutation();
  /** For single share */
  const [order, setOrder] = useState<Order | null>(null);

  const [isSending, setIsSending] = useState<boolean>(false);
  const selectedTripId = useAppSelector((state) => state.trip.tripId);
  const { data: driverLinks } = useGetDriverLinksForTripQuery(
    selectedTripId || tripId || skipToken,
    {
      skip: !open || (!selectedTripId && !tripId)
    }
  );

  const { data: orderDriverLinks } = useGetDriverLinksForOrderQuery(orderId || skipToken, {
    skip: !open || !orderId
  });

  const messageTypeForDriver = MESSAGE_TYPE.SHARE;
  const shareEntityType = (selectedTripId || tripId) && enableLtl ? 'trip' : 'order';
  const shareReadableId =
    (selectedTripId || tripId) && enableLtl
      ? driverLinks?.trip?.readable_id
      : order?.readable_id || '';

  // map of trips where value is map of drivers and value is driverLink
  const [tripDriversLinkMaps, setTripDriversLinkMaps] =
    useState<Map<string, Map<string, string>>>();

  // useEffect on orderDriverLinks if changes set load to order object
  const initDriverLinks = (): void => {
    if (!driverLinks || !driverLinks.trip) return;
    const driverLinksMap = new Map<string, string>();
    driverLinks.drivers.forEach((driver) => {
      driverLinksMap.set(driver.driver.id, driver.link);
    });
    const mapOfDriversAndLinks = new Map<string, Map<string, string>>();
    mapOfDriversAndLinks.set(driverLinks.trip.id, driverLinksMap);
    setTripDriversLinkMaps(mapOfDriversAndLinks);
  };

  const initOrderDriverLinks = (): void => {
    // return if data is null
    if (!orderDriverLinks) return;
    if (orderDriverLinks && orderDriverLinks.trips) {
      setOrder(orderDriverLinks.order);
      const tripDriversLinkMap = new Map<string, Map<string, string>>();

      orderDriverLinks.trips.forEach((trip) => {
        const driversLinkMap = new Map<string, string>();
        trip.drivers.forEach((driver) => {
          driversLinkMap.set(driver.driver.id, driver.link);
        });
        tripDriversLinkMap.set(trip.trip.id, driversLinkMap);
      });
      setTripDriversLinkMaps(tripDriversLinkMap);
    }
  };

  /** Generates data to send update to assigned drivers */
  const initAssignedDriversPayload = useCallback(() => {
    if (open) {
      const listOfAssignedDrivers = initDriversPayLoadListForMessageType(
        messageTypeForDriver,
        driversToNotify
      );
      setAssignedPayload(listOfAssignedDrivers);
      initDriverLinks(); // Call initDriverLinks after setting assigned payload
      initOrderDriverLinks(); // Call initOrderDriverLinks after setting assigned payload
    }
  }, [open, driversToNotify, orderDriverLinks, driverLinks, order?.id]);

  const initDriversPayLoadListForMessageType = (
    messageType: MESSAGE_TYPE,
    driversList: INotifyDriverWithFullSegment[]
  ): OrderSharePreviewParams[] => {
    const allInfo = driversList?.map((notifyTo: INotifyDriverWithFullSegment, i: number) => {
      const find = drivers?.content.find((driver) => driver.id === notifyTo.driverId);

      const msgType = notifyTo.isForExistingLoadUpdate ? MESSAGE_TYPE.UPDATE : messageType;
      const sms = new ShareTripSMS({
        messageType: msgType,
        company: {
          company_name: userData?.tenant?.company_name || '',
          short_offset: userData?.short_offset || ''
        },
        use24HourClock: use24HourClock,
        trip: notifyTo.fullSegment,
        order: order,
        ltl_enabled: enableLtl,
        useCustomerOrderNumber: useCustomerOrderNumber
      });

      const smsText = sms.getMessage();
      notifyTo.textMessage = smsText;
      return {
        driver_name: find?.name,
        driver_id: notifyTo.driverId,
        trip_id: notifyTo.fullSegment.id,
        via_sms: !!find?.phone,
        via_email: !!find?.email_address && !find?.phone,
        message_type: msgType,
        deleted_trip_index: notifyTo.deleted_trip_index,
        text_message: smsText ? smsText : undefined
      };
    });

    return allInfo;
  };

  /** Generates data to send update to unassigned drivers */
  const initUnassignedDriversPayload = useCallback(() => {
    if (open) {
      const listOfUnassignedDrivers = initDriversPayLoadListForMessageType(
        MESSAGE_TYPE.UNASSIGNED,
        unassignedDriversToNotify
      );

      setUnassignedPayload(listOfUnassignedDrivers);
    }
  }, [open, unassignedDriversToNotify, order?.id]);

  const handleClose = (): void => {
    setAssignedPayload([]);
    setUnassignedPayload([]);
    setIsSending(false);
    setOrder(null);
    onClose && onClose();
  };

  useEffect(() => {
    open && driversToNotify && initAssignedDriversPayload();
  }, [driversToNotify, open, orderDriverLinks, driverLinks, order?.id]);

  useEffect(() => {
    open && unassignedDriversToNotify && initUnassignedDriversPayload();
  }, [unassignedDriversToNotify, open, order?.id]);

  const sendUpdate = async (): Promise<void> => {
    setIsSending(true);
    const combinedPayload = [...(assignedPayload || []), ...(unassignedPayload || [])];

    try {
      if (combinedPayload && combinedPayload?.length > 0) {
        let cleanPayload: OrderSharePreviewParams[] = [];
        cleanPayload = combinedPayload.map((r) => ({
          driver_id: r.driver_id,
          driver_name: r.driver_name,
          via_email: r.via_email,
          via_sms: r.via_sms,
          trip_id: r.trip_id,
          message_type: r.message_type,
          deleted_trip_index: r.deleted_trip_index,
          text_message: r.via_sms
            ? r.text_message
              ? `${r.text_message}\n(Do not reply - automated text)`
              : undefined
            : undefined
        }));

        cleanPayload = cleanPayload.filter((obj) => obj.via_email || obj.via_sms);

        const successfulRows: OrderSharePreviewParams[] = [];
        const failedRows: {
          share: OrderSharePreviewParams;
          error: { status?: number; data?: { message?: string } };
        }[] = [];

        for (let i = 0; i < cleanPayload.length; i++) {
          const { trip_id: tripId, ...instructions } = cleanPayload[i];
          if (!tripId) continue;
          await shareTripPreview({
            id: tripId,
            instructions: { ...instructions, order_id: orderId || undefined },
            searchTripParams: {
              filters: {
                ...filterParams?.filters
              }
            }
          })
            .unwrap()
            .then((response) => {
              if (response === 200) {
                successfulRows.push(cleanPayload[i]);
              }
            })
            .catch((error) => {
              console.error('Error sending sms', error);

              failedRows.push({ share: cleanPayload[i], error: error });
            });
        }

        if (failedRows.length > 0) {
          // make list of driver names
          const driverNames = failedRows.map((row) => row.share.driver_name).join(', ');

          // find a row if it has error status 409
          const conflictRow = failedRows.find((row) => row.error.status === 409);

          if (conflictRow) {
            addNotification({
              message: `Unable to send order update to driver(s) ${driverNames}. ${conflictRow.error.data?.message}`,
              variant: 'error'
            });
          } else {
            addNotification({
              message: `Unable to send order update to driver(s) ${driverNames} . Please check recipient [phone number/email] and try again`,
              variant: 'error'
            });
          }
          logRocketTrack('Dispatch', {
            action: 'Dispatch to driver',
            severity: 'low',
            tenant_id: userData?.tenant?.id,
            company_name: userData?.tenant?.company_name,
            email: userData?.email_address,
            user_first_name: userData?.first_name,
            user_last_name: userData?.last_name
          });
        } else if (successfulRows.length > 0) {
          const allShareMessages = successfulRows.every((row) => row.message_type === 'SHARE');
          const allDriversHaveNames = successfulRows.every((row) => row.driver_name);
          if (allShareMessages && allDriversHaveNames) {
            const driverNames = successfulRows.map((row) => row.driver_name).join(', ');
            addNotification({
              message: `${
                shareEntityType == 'trip' ? 'Trip' : 'Order'
              } ${shareReadableId} has been sent to ${driverNames}`,
              variant: 'success'
            });
          } else if (allDriversHaveNames) {
            const driverNames = successfulRows.map((row) => row.driver_name).join(', ');
            addNotification({
              message: `${
                shareEntityType == 'trip' ? 'Trip' : 'Order'
              } update has been sent to ${driverNames}`,
              variant: 'success'
            });
          } else {
            addNotification({
              message: `${shareEntityType == 'trip' ? 'Trip' : 'Order'} update has been sent`,
              variant: 'success'
            });
          }
        }
      }
    } catch (e) {
      console.error(e);
      logRocketTrack('Dispatch', {
        action: 'Dispatch to driver',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: `Unable to send order update to driver. Please check recipient [phone number/email] and try again`,
        variant: 'error'
      });
      setIsSending(false);
    } finally {
      handleClose();
    }
  };

  const { updatePayloadForAssigned, updatePayloadForUnassigned } = useDriverPayload({
    assignedPayload,
    unassignedPayload,
    setAssignedPayload,
    setUnassignedPayload,
    messageTypeForDriver
  });

  const { getAssignedDriverLabel, getUnassignedDriverLabel } = useDriverLabels({
    isSplitLoad,
    assignedDrivers: driversToNotify,
    unassignedDrivers: unassignedDriversToNotify
  });

  const shareModalTitle = `Share ${shareEntityType} ${
    (selectedTripId || tripId) && enableLtl
      ? driverLinks?.trip?.readable_id
        ? `#${driverLinks?.trip?.readable_id}`
        : ''
      : order?.readable_id
      ? `#${order?.readable_id}`
      : ''
  }`;

  const shareHistoryEntityId = enableLtl ? selectedTripId || tripId : orderId;

  const isShareDisabled =
    (assignedPayload?.length === 0 && unassignedPayload?.length === 0) ||
    isSending ||
    ![...assignedPayload, ...unassignedPayload].some(
      (payload) => payload.via_email || payload.via_sms
    );

  const canRemoveDrivers = useCanRemoveDrivers(
    driversToNotify,
    assignedPayload,
    unassignedDriversToNotify,
    unassignedPayload
  );

  return (
    <>
      <TwDialog
        title={shareModalTitle}
        size="sm"
        open={open}
        fullScreen={matchesMobileOrTablet}
        onClickClose={handleClose}
        sx={{ '& .MuiDialogContent-root': { padding: '0 !important' } }}
      >
        <>
          {/* Assigned Drivers Section */}
          <DriverNotificationPreferenceList
            type="assigned"
            drivers={driversToNotify}
            payload={assignedPayload}
            tripDriversLinkMaps={tripDriversLinkMaps}
            onUpdateDriver={updatePayloadForAssigned}
            getDriverLabel={getAssignedDriverLabel}
            canRemove={canRemoveDrivers}
          />

          {/* Unassigned Drivers Section */}
          <DriverNotificationPreferenceList
            type="unassigned"
            drivers={unassignedDriversToNotify}
            payload={unassignedPayload}
            tripDriversLinkMaps={null} // No links for unassigned drivers
            onUpdateDriver={updatePayloadForUnassigned}
            getDriverLabel={getUnassignedDriverLabel}
            canRemove={canRemoveDrivers}
          />

          <Box className="notify-driver__history">
            <TwShareHistory
              id={shareHistoryEntityId || ''}
              shareType={ShareType.load_driver_link}
            />
          </Box>
        </>

        <Stack
          className="notify-driver__container"
          spacing={1}
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          width="100%"
        >
          <TwButton label="Skip" action={handleClose} type={TwButtonType.secondary} />
          <TwButton
            label="Share"
            action={sendUpdate}
            type={TwButtonType.primary}
            disabled={isShareDisabled}
          />
        </Stack>
      </TwDialog>
    </>
  );
};

TwNotifyDriver.defaultProps = {
  isSplitLoad: false
};

export default TwNotifyDriver;
